import React, { useCallback, useEffect, useState } from "react";
import { Download, PlusSquare, Share, X } from "lucide-react";
import { Button, Sheet } from "./ui";

/*
 * "Install Cricso" for phones.
 *
 * Android (Chrome / Edge / Samsung Internet) fires `beforeinstallprompt`; we keep the event
 * (public/index.html also catches it early, before React mounts) and call prompt() from our
 * own button. iOS has no install API, so there we show the Share → "Add to Home Screen" steps.
 */

const DISMISS_KEY = "cricso-install-dismissed-at";
const SNOOZE_DAYS = 7;

const isStandalone = () =>
  window.matchMedia?.("(display-mode: standalone)").matches || window.navigator.standalone === true;

const isIos = () => {
  const ua = window.navigator.userAgent || "";
  // iPadOS reports itself as a Mac; touch support gives it away
  return /iphone|ipad|ipod/i.test(ua) || (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1);
};

const isIosSafari = () => isIos() && !/crios|fxios|edgios/i.test(window.navigator.userAgent);

function snoozed() {
  try {
    const at = Number(localStorage.getItem(DISMISS_KEY) || 0);
    return at && Date.now() - at < SNOOZE_DAYS * 86400000;
  } catch {
    return false;
  }
}

function snooze() {
  try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch { /* private mode */ }
}

/** Install state shared by the popup and the Profile button. */
export function useInstall() {
  const [deferred, setDeferred] = useState(() => window.__cricsoInstallPrompt || null);
  const [installed, setInstalled] = useState(isStandalone);

  useEffect(() => {
    const onPrompt = (e) => { e.preventDefault(); window.__cricsoInstallPrompt = e; setDeferred(e); };
    const onInstalled = () => { window.__cricsoInstallPrompt = null; setDeferred(null); setInstalled(true); };
    const onEarly = () => setDeferred(window.__cricsoInstallPrompt); // caught by index.html before we mounted
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);
    window.addEventListener("cricso-install-available", onEarly);
    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
      window.removeEventListener("cricso-install-available", onEarly);
    };
  }, []);

  const install = useCallback(async () => {
    if (!deferred) return false;
    deferred.prompt();
    const { outcome } = await deferred.userChoice;
    window.__cricsoInstallPrompt = null;
    setDeferred(null); // the event can only be used once
    if (outcome === "accepted") setInstalled(true);
    return outcome === "accepted";
  }, [deferred]);

  const platform = installed ? null : deferred ? "android" : isIos() ? "ios" : null;
  return { platform, install, iosSafari: isIosSafari() };
}

/** Bottom popup, shown once per visit (snoozed for a week when closed). */
export default function InstallPrompt() {
  const { platform, install, iosSafari } = useInstall();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!platform || snoozed()) return setVisible(false);
    const t = setTimeout(() => setVisible(true), 2500); // let the page settle first
    return () => clearTimeout(t);
  }, [platform]);

  if (!visible || !platform) return null;

  const close = () => { snooze(); setVisible(false); };

  return (
    <div
      className="fixed inset-x-0 bottom-0 z-50 flex justify-center px-3 animate-[slideUp_.3s_ease-out]"
      style={{ paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 12px)" }}
      role="dialog"
      aria-label="Install Cricso"
      data-testid="install-prompt"
    >
      <div className="w-full max-w-[456px] rounded-3xl bg-white shadow-[0_12px_40px_rgba(0,0,0,0.18)] border border-black/[0.06] p-4">
        <div className="flex items-start gap-3">
          <img src="/icon-192.png" alt="" className="h-12 w-12 rounded-2xl shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="font-bold text-ink">Install Cricso</div>
            <div className="text-sm text-ink-muted leading-snug">
              Get the app on your home screen — opens full screen, faster, like a normal app.
            </div>
          </div>
          <button onClick={close} className="h-8 w-8 -mr-1 -mt-1 grid place-items-center rounded-full text-ink-muted hover:bg-black/5 shrink-0" aria-label="Not now" data-testid="install-close">
            <X size={18} />
          </button>
        </div>

        {platform === "android" ? (
          <div className="grid grid-cols-2 gap-2 mt-3">
            <Button variant="outline" size="sm" className="h-11" onClick={close}>Not now</Button>
            <Button size="sm" className="h-11" onClick={async () => { await install(); setVisible(false); }} data-testid="install-btn">
              <Download size={16} /> Install
            </Button>
          </div>
        ) : (
          <IosSteps safari={iosSafari} className="mt-3" />
        )}
      </div>
    </div>
  );
}

function IosSteps({ safari, className }) {
  return (
    <ol className={`space-y-2 text-sm ${className || ""}`} data-testid="install-ios-steps">
      <li className="flex items-center gap-2.5 rounded-2xl bg-black/[0.04] px-3 py-2.5">
        <span className="h-6 w-6 rounded-full bg-brand text-white text-xs font-bold grid place-items-center shrink-0">1</span>
        <span className="flex-1">
          Tap <b>Share</b> <Share size={15} className="inline -mt-1 text-[#007AFF]" />
          {safari ? " in the bar at the bottom" : " in the address bar"}
        </span>
      </li>
      <li className="flex items-center gap-2.5 rounded-2xl bg-black/[0.04] px-3 py-2.5">
        <span className="h-6 w-6 rounded-full bg-brand text-white text-xs font-bold grid place-items-center shrink-0">2</span>
        <span className="flex-1">
          Choose <b>Add to Home Screen</b> <PlusSquare size={15} className="inline -mt-1" />
        </span>
      </li>
    </ol>
  );
}

/** Always-available "Install app" button (e.g. on Profile), for people who closed the popup. */
export function InstallAppButton({ className }) {
  const { platform, install, iosSafari } = useInstall();
  const [iosOpen, setIosOpen] = useState(false);
  if (!platform) return null;
  return (
    <>
      <Button
        variant="outline"
        size="md"
        className={className}
        onClick={() => (platform === "android" ? install() : setIosOpen(true))}
        data-testid="profile-install-btn"
      >
        <Download size={16} /> Install Cricso app
      </Button>
      <Sheet open={iosOpen} onClose={() => setIosOpen(false)} title="Install Cricso">
        <IosSteps safari={iosSafari} />
      </Sheet>
    </>
  );
}
